import React, { useEffect, useState } from 'react';

import AtlasMetadataClient from '@reuters-graphics/graphics-atlas-client';
import BeeswarmChart from '@reuters-graphics/chart-module-vaccination-income/dist/index.js';
import LoaderPanel from 'Components/common/LoaderPanel';
import classnames from 'classnames';
import { component } from './styles.module.scss';
import debounce from 'lodash/debounce';
import useObservable from 'Components/hooks/observed';
import useWindowDimensions from 'Components/hooks/useWindowDimensions';

const atlas = new AtlasMetadataClient();

const chart = new BeeswarmChart();

const Pace = (props) => {
  const chartContainer = React.createRef();
  const { vaccinationSummaryStatistics } = props;
  const [metric, setMetric] = useState('region');
  const [chartDrawn, setChartDrawn] = useState(false);
  const isVisible = useObservable({ ref: chartContainer, rootMargin: '600px' });

  const { width: windowWidth } = useWindowDimensions();

  const resize = debounce(() => {
    chart.draw();
  }, 250);

  useEffect(() => {
    if(!isVisible) return;
    chart
      .selection('.vaccination-income-chart')
      .data(vaccinationSummaryStatistics)
      .props({
        yMetric: metric,
        padding: 2,
        margin: {
          top: 20,
          right: windowWidth <= 475 ? 15 : 20,
          bottom: 25,
          left: windowWidth > 875 ? 40 : windowWidth > 475 ? 15 : 15,
        },
        colorStroke: '#A3BE8C',
        axisText: windowWidth > 650 ? 'Percent of population given at least one dose of vaccine' : 'Percent given at least 1 dose of vaccine',
        height: windowWidth > 600 ? 735 : 625,
        maxRadius: windowWidth > 600 ? 35 : 25,
      })
      .draw();
    setChartDrawn(true);
  }, [vaccinationSummaryStatistics, metric, windowWidth, isVisible]);

  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  // dataProfile.income.IncomeGroup
  const stats = vaccinationSummaryStatistics
    .filter(c => c.peopleVaccinated)
    .map((d) => {
      const { countryISO: isoAlpha2, region, population, peopleVaccinated } = d;
      const country = atlas.getCountry(isoAlpha2);
      const income = country.dataProfile.income ? country.dataProfile.income.IncomeGroup : null;
      return {
        isoAlpha2,
        population,
        region,
        income,
        peopleVaccinated,
      };
    });
  const totalVaccinated = stats.reduce((a, d) => a + d.peopleVaccinated, 0);
  const vaccinatedInEuropeAndNorthAmerica = stats
    .filter(d => d.region === 'Europe' || d.region === 'Northern America')
    .reduce((a, d) => a + d.peopleVaccinated, 0);
  const vaccinatedInHighIncome = stats
    .filter(d => d.income === 'High income')
    .reduce((a, d) => a + d.peopleVaccinated, 0);

  const percentInHighIncome = Math.floor(vaccinatedInHighIncome / totalVaccinated * 100);
  const percentInEuropeAndNorthAmerica = Math.floor(vaccinatedInEuropeAndNorthAmerica / totalVaccinated * 100);

  return (
    <div className={classnames(component)}>
      <section>
        <h4>Do some countries have an advantage?</h4>
        <p>Yes, generally richer and more developed countries have better health care infrastructure to manufacture, acquire and administer doses.</p>
        <p>About <strong>{percentInHighIncome.toLocaleString('en')}%</strong> of people who have received at least one dose of a coronavirus vaccine were from high income countries, and at least <strong>{percentInEuropeAndNorthAmerica.toLocaleString('en')}%</strong> were from Europe and North America. (Again, that only includes data from countries that report these figures.)
        </p>
        <div className='section-spacer-internal' />
      </section>

      <section className=''>
        <div className='options font-sans'>
          <span>Show by</span>
          <div className='options-group'>
            <button
              className={classnames({ active: metric === 'region' })}
              onClick={() => setMetric('region')}
            >
              Region
            </button>
            <button
              className={classnames({ active: metric === 'IncomeGroup' })}
              onClick={() => setMetric('IncomeGroup')}
            >
              Income
            </button>
          </div>
        </div>
      </section>
      <section className='wide'>
        <div className='vaccination-income-chart' ref={chartContainer} />
        {!chartDrawn && (
          <LoaderPanel height={windowWidth > 600 ? 735 : 625} />
        )}
        <p className='footnote'>Note: Income classifications from The World Bank</p>
      </section>
    </div>
  );
};

export default Pace;
